import { LockIcon } from "@/components/atoms/icons";
import { ContentDialog } from "@/components/molecules/dialogs/ContentDialog";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useUIColors } from "@/core/hooks";
import {
  trackCatalogEvent,
  trackGuestLoginPromptMaybeLaterClick,
} from "@/core/services/trackDynatraceCatalog";
import { useGuestLabels } from "@/modules/guest-web/hooks/useGuestLabels";
import React, { useEffect, useRef } from "react";
import { StyleSheet, View } from "react-native";

export interface GuestLoginPromptDialogMobileProps {
  readonly visible: boolean;
  readonly onMaybeLater: () => void;
  readonly onLogin: () => void;
}

/**
 * Mobile-only login prompt — same content/copy as the shared
 * GuestLoginPromptDialog (guest-web, used by web too), just with more
 * breathing room between the icon/title/message/buttons than that shared
 * dialog's compact-phone spacing, per design feedback. Built directly on
 * the generic ContentDialog shell rather than editing the shared component.
 */
export function GuestLoginPromptDialogMobile({
  visible,
  onMaybeLater,
  onLogin,
}: Readonly<GuestLoginPromptDialogMobileProps>) {
  const colors = useUIColors();
  const labels = useGuestLabels();
  const viewTrackedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      viewTrackedRef.current = false;
      return;
    }
    if (viewTrackedRef.current) {
      return;
    }
    viewTrackedRef.current = true;
    trackCatalogEvent({
      screen: DT_SCREEN.GUEST,
      actionName: "guest_login_prompt_view",
      eventType: "view",
    });
  }, [visible]);

  const handleLoginPress = () => {
    trackCatalogEvent({
      screen: DT_SCREEN.GUEST,
      actionName: "guest_login_prompt_login_click",
      eventType: "action",
    });
    onLogin();
  };

  const handleMaybeLaterPress = () => {
    trackGuestLoginPromptMaybeLaterClick();
    onMaybeLater();
  };

  return (
    <ContentDialog
      visible={visible}
      onClose={onMaybeLater}
      contentAlign="center"
      maxWidth={340}
      cardStyle={styles.card}
      headerStyle={styles.header}
      actionsStyle={styles.actions}
      icon={
        <View style={styles.iconWrap}>
          <LockIcon size={48} color={colors.fontPrimary} />
        </View>
      }
      title={labels.loginPromptTitle}
      titleVariant="headline"
      titleColor="primary"
      titleStyle={styles.title}
      subtitle={labels.loginPromptMessage}
      subtitleVariant="body"
      subtitleColor="secondary"
      subtitleStyle={styles.subtitle}
      actions={[
        {
          label: labels.maybeLater,
          variant: "outline",
          flex: true,
          onPress: handleMaybeLaterPress,
          style: { backgroundColor: colors.backgroundLevel1 },
        },
        {
          label: labels.login,
          variant: "primary",
          flex: true,
          onPress: handleLoginPress,
        },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    paddingTop: 32,
    paddingBottom: 24,
    paddingHorizontal: 20,
    gap: 24,
  },
  header: {
    gap: 16,
  },
  iconWrap: {
    marginBottom: 4,
  },
  title: {
    letterSpacing: 0,
    textAlign: "center",
  },
  subtitle: {
    letterSpacing: 0,
    lineHeight: 21,
    textAlign: "center",
  },
  actions: {
    width: "100%",
    gap: 16,
    marginTop: 8,
  },
});

export default GuestLoginPromptDialogMobile;
